/**
 * Wall drawing tool
 * - Click to place waypoints (polyline)
 * - Shows preview line from last point to cursor
 * - Double-click or Enter: finish wall (creates Wall in stage-store)
 * - Escape: cancel current wall
 * - Minimum 2 points required
 * - Creates wall with default height 2.4m and 'plywood' material
 */

import { Vec2, snapToGrid } from '@/canvas/coordinates';
import { ToolHandler, ToolContext } from './tool-handler';
import { useEditorStore } from '@/core/stores/editor-store';
import { useStageStore } from '@/core/stores/stage-store';
import { useHistoryStore } from '@/core/stores/history-store';
import * as defaults from '@/stage/defaults';
import { toast } from 'sonner';

interface WallToolState {
  points: Vec2[];
  lastClickTime: number;
  previewPos: Vec2 | null;
}

const state: WallToolState = {
  points: [],
  lastClickTime: 0,
  previewPos: null,
};

const DOUBLE_CLICK_THRESHOLD = 300; // milliseconds
const MIN_WALL_HEIGHT = 2.4; // meters

export function getWallToolHandler(): ToolHandler {
  return {
    onMouseDown(ctx: ToolContext) {
      if (ctx.button !== 0) return; // Only left-click

      const { snapToGrid: shouldSnap, gridSize } = useEditorStore.getState();

      // Determine position with optional snap
      const pos = shouldSnap ? snapToGrid(ctx.worldPos, gridSize) : ctx.worldPos;

      // Check for double-click
      const now = Date.now();
      const isDoubleClick = now - state.lastClickTime < DOUBLE_CLICK_THRESHOLD;
      state.lastClickTime = now;

      if (isDoubleClick && state.points.length >= 2) {
        // Finish wall
        finishWall();
        return;
      }

      // Add point
      state.points.push(pos);
    },

    onMouseMove(ctx: ToolContext) {
      const { snapToGrid: shouldSnap, gridSize } = useEditorStore.getState();

      state.previewPos = shouldSnap ? snapToGrid(ctx.worldPos, gridSize) : ctx.worldPos;
    },

    onKeyDown(key: string) {
      if (key === 'Escape') {
        // Cancel wall drawing
        state.points = [];
        state.previewPos = null;
      } else if (key === 'Enter' && state.points.length >= 2) {
        // Finish wall
        finishWall();
      }
    },

    getCursor() {
      return 'crosshair';
    },

    renderPreview(ctx2d: CanvasRenderingContext2D, _zoom: number) {
      if (state.points.length === 0) return;

      // Draw all placed points
      ctx2d.fillStyle = 'rgba(100, 150, 255, 0.8)';
      ctx2d.strokeStyle = 'rgba(50, 100, 255, 1)';
      ctx2d.lineWidth = 0.1;

      // Draw lines connecting points
      if (state.points.length > 1) {
        ctx2d.beginPath();
        ctx2d.moveTo(state.points[0].x, state.points[0].y);
        for (let i = 1; i < state.points.length; i++) {
          ctx2d.lineTo(state.points[i].x, state.points[i].y);
        }
        ctx2d.stroke();
      }

      // Draw line from last point to cursor
      if (state.previewPos && state.points.length > 0) {
        ctx2d.strokeStyle = 'rgba(100, 150, 255, 0.5)';
        ctx2d.setLineDash([0.2, 0.2]);
        ctx2d.beginPath();
        ctx2d.moveTo(state.points[state.points.length - 1].x, state.points[state.points.length - 1].y);
        ctx2d.lineTo(state.previewPos.x, state.previewPos.y);
        ctx2d.stroke();
        ctx2d.setLineDash([]);
      }

      // Draw point circles
      for (const point of state.points) {
        ctx2d.beginPath();
        ctx2d.arc(point.x, point.y, 0.15, 0, Math.PI * 2);
        ctx2d.fill();
      }

      // Draw preview point
      if (state.previewPos) {
        ctx2d.fillStyle = 'rgba(100, 150, 255, 0.4)';
        ctx2d.beginPath();
        ctx2d.arc(state.previewPos.x, state.previewPos.y, 0.15, 0, Math.PI * 2);
        ctx2d.fill();
      }
    },
  };
}

function finishWall() {
  if (state.points.length < 2) {
    toast.error('A wall needs at least two points');
    return;
  }

  const stageState = useStageStore.getState();
  const historyState = useHistoryStore.getState();
  const editorState = useEditorStore.getState();
  const { layout } = stageState;

  if (!layout) return;

  const wall = defaults.createDefaultWall('wall', state.points);
  wall.height_m = MIN_WALL_HEIGHT;

  historyState.pushState(layout);
  stageState.addWall(wall);
  editorState.setDirty(true);

  const segments = state.points.length - 1;
  toast.success(
    `Wall added (${segments} ${segments === 1 ? 'segment' : 'segments'})`,
  );

  state.points = [];
  state.previewPos = null;
  state.lastClickTime = 0;
}

/** Clear any in-progress polyline — call this when switching away from the wall tool. */
export function resetWallTool(): void {
  state.points = [];
  state.previewPos = null;
  state.lastClickTime = 0;
}
